import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axiosInstance from "../../api/axios";

// BD requests that were accepted but missed the numbers. The plan lets a social
// following stand in for them, and only a person can judge that, so these wait
// here instead of being refused outright.
const BD = "/api/admin/user/bd-requests/review";

const root = ["bd-review"];

// ErrorBanner only prints a useful heading for an ApiError; this instance throws
// a raw AxiosError, so surface the backend's own message.
const unwrap = (p) =>
  p.catch((e) => {
    throw new Error(e?.response?.data?.message || e.message);
  });

// A plain array, not the paginated envelope the rest of the panel uses.
export function useBdReviewList() {
  return useQuery({
    queryKey: [...root, "list"],
    queryFn: () => unwrap(axiosInstance.get(BD).then((r) => r.data.data)),
    refetchOnWindowFocus: true, // live work list
    staleTime: 0,
  });
}

// onSettled, not onSuccess: if another admin decided this row first the call
// fails, and the stale list still has to be dropped and refetched.
const useDecision = (mutationFn) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: [...root, "list"] }),
  });
};

// Capacity is re-read at the moment of approval, not taken from when the request
// was forwarded, so this can answer "Please wait until your sponsor has
// available spot" — a normal outcome rather than a fault.
export const useApproveBdReview = () =>
  useDecision(({ requestId, note }) =>
    axiosInstance.post(`${BD}/approve`, { requestId, note }),
  );

export const useRejectBdReview = () =>
  useDecision(({ requestId, note }) =>
    axiosInstance.post(`${BD}/reject`, { requestId, note }),
  );

// User.socialLinks on the backend model. Shown in this order; blanks are dropped.
export const SOCIAL_FIELDS = [
  { key: "telegram", label: "Telegram" },
  { key: "facebook", label: "Facebook" },
  { key: "twitter", label: "Twitter" },
  { key: "instagram", label: "Instagram" },
  { key: "linkedIn", label: "LinkedIn" },
];
